import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, ArrowDropDown, ArrowDropUp, Delete, PlayCircle } from '@mui/icons-material'
import { Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import Player from '@vimeo/player'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { InputTranslator } from '../InputTranslator/InputTranslator'
import { addChapterAt, CHAPTER_NUDGE_MS, EditableFtueChapter, formatChapterTime, nudgeChapter, removeChapterAt, setChapterTitles } from './ftueChapterHelpers'

type FtueChapterEditorProps = {
  chapters: EditableFtueChapter[]
  onChange: (chapters: EditableFtueChapter[]) => void
  /** The Vimeo player of the video, e.g. from `FtueVideoPlayer`'s `onPlayerReady`. Adding and seeking wait for it. */
  player?: Player
  disabled?: boolean
  /** Extra controls under a chapter's title, such as FTUE chapter tags. */
  renderChapterExtra?: (chapter: EditableFtueChapter, index: number) => ReactNode
}

type RemoveConfirmData = { index: number }

/**
 * The chapter list of a video, kept in time order and synced with its player: add a chapter at the
 * player's current time, move one by a second, play from it, translate its title, or remove it.
 */
export const FtueChapterEditor: FC<FtueChapterEditorProps> = ({ chapters, onChange, player, disabled, renderChapterExtra }) => {
  const { t } = useTranslation()
  const [durationMs, setDurationMs] = useState<number>()
  const [playerFailed, setPlayerFailed] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<RemoveConfirmData>>()

  useEffect(() => {
    let active = true
    setDurationMs(undefined)
    player
      ?.getDuration()
      .then((seconds) => active && setDurationMs(seconds * 1000))
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [player])

  const addChapter = async () => {
    if (!player) return
    try {
      const seconds = await player.getCurrentTime()
      setPlayerFailed(false)
      onChange(addChapterAt(chapters, seconds, durationMs))
    } catch {
      setPlayerFailed(true)
    }
  }

  const playFrom = async (chapter: EditableFtueChapter) => {
    if (!player) return
    try {
      await player.setCurrentTime(chapter.dataSeek / 1000)
      setPlayerFailed(false)
      await player.play()
    } catch {
      setPlayerFailed(true)
    }
  }

  const confirmRemove = (chapter: EditableFtueChapter, index: number) => {
    setConfirmDialogData({
      title: t('internal-common:ftue_chapters_remove'),
      confirmText: t('internal-common:ftue_chapters_remove_confirm', { time: formatChapterTime(chapter.dataSeek), title: chapter.titles?.en || '---' }),
      actionText: t('internal-common:ftue_chapters_remove'),
      destructiveAction: true,
      data: { index },
    })
  }

  const handleConfirm = (confirmed?: ConfirmDialogData<RemoveConfirmData>) => {
    setConfirmDialogData(undefined)
    const index = confirmed?.data?.index
    if (index !== undefined) onChange(removeChapterAt(chapters, index))
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} spacing={2}>
        <Typography variant="h6">{t('internal-common:ftue_chapters')}</Typography>
        <Tooltip title={player ? '' : t('internal-common:ftue_chapters_waiting_for_player')}>
          <span>
            <Button variant="outlined" startIcon={<Add />} onClick={addChapter} disabled={!player || disabled}>
              {t('internal-common:ftue_chapters_add')}
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {playerFailed && (
        <Typography variant="body2" color="error.main" mb={1}>
          {t('internal-common:ftue_chapters_player_failed')}
        </Typography>
      )}

      {chapters.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('internal-common:ftue_chapters_none')}
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {chapters.map((chapter, index) => {
            const time = formatChapterTime(chapter.dataSeek)
            return (
              <Stack key={chapter.chapterId ?? `new-${index}`} direction="row" alignItems="center" spacing={1} py={1} data-testid="ftue-chapter-row">
                <Tooltip title={t('internal-common:ftue_chapters_play', { time })}>
                  <span>
                    <IconButton
                      aria-label={t('internal-common:ftue_chapters_play', { time })}
                      color="primary"
                      onClick={() => playFrom(chapter)}
                      disabled={!player}
                    >
                      <PlayCircle />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography sx={{ minWidth: (theme) => theme.spacing(6), fontVariantNumeric: 'tabular-nums' }}>{time}</Typography>
                <Stack>
                  <IconButton
                    size="small"
                    aria-label={t('internal-common:ftue_chapters_later', { time })}
                    onClick={() => onChange(nudgeChapter(chapters, index, CHAPTER_NUDGE_MS, durationMs))}
                    disabled={disabled}
                    sx={{ p: 0 }}
                  >
                    <ArrowDropUp />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t('internal-common:ftue_chapters_earlier', { time })}
                    onClick={() => onChange(nudgeChapter(chapters, index, -CHAPTER_NUDGE_MS, durationMs))}
                    disabled={disabled || chapter.dataSeek <= 0}
                    sx={{ p: 0 }}
                  >
                    <ArrowDropDown />
                  </IconButton>
                </Stack>
                <Box flex={1} minWidth={0}>
                  <InputTranslator
                    value={chapter.titles}
                    onChange={(titles) => onChange(setChapterTitles(chapters, index, titles))}
                    dialogTitle={t('internal-common:ftue_chapters_title_dialog', { time })}
                    label={t('internal-common:ftue_chapters_title')}
                    disabled={disabled}
                  />
                  {renderChapterExtra?.(chapter, index)}
                </Box>
                <Tooltip title={t('internal-common:ftue_chapters_remove')}>
                  <span>
                    <IconButton
                      aria-label={t('internal-common:ftue_chapters_remove_at', { time })}
                      color="error"
                      onClick={() => confirmRemove(chapter, index)}
                      disabled={disabled}
                    >
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )
          })}
        </Stack>
      )}

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
